import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { FC, memo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { applyApiErrors, VALIDATION_TYPES } from "@/components/Formik";
import { FormikSelect } from "@/components/Select";
import { Text } from "@/components/Text";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_MD } from "@/core/media";
import { IConsultantProfile, updateConsultantProfileSettings } from "@/data/consultant-profiles";
import { TUserRole } from "@/data/session";
import { updateProfileRole } from "@/data/users";
import { MessagesContext } from "@/eworkitComponents";
import { Flex, Group, Label } from "@/eworkitComponents";
import { FormikForm } from "@/formikComponents";
import { IConsultantProfileSectionProps } from "@/views/components/ConsultantProfile/Sections/types";
import { FormikRadio } from "@/views/components/Formik";

export const validationSettingsSchema = Yup.object().shape({
  searchable: Yup.boolean().required(VALIDATION_TYPES.REQUIRED),
});

export interface ISettingsSectionFormValues {
  searchable: boolean;
  companyRole: TUserRole | null;
}

interface ISettingsSectionFormProps {
  form: FormikProps<ISettingsSectionFormValues>;
}

export const SETTINGS_MESSENGER_CONTEXT = "profile-settings-form";

interface Props extends IConsultantProfileSectionProps, ISettingsSectionFormProps, IEditableSectionRenderProps {
  isOwner: boolean;
  isAdmin: boolean;
  isManager: boolean;
  role: string;
}

export interface IRoleOption {
  code: TUserRole;
  value: string;
}

type TRolesMap = { [key in TUserRole]: IRoleOption };

export const rolesList: TRolesMap = {
  MANAGER: {
    code: "MANAGER",
    value: "MANAGER",
  },
  CONSULTANT: {
    code: "CONSULTANT",
    value: "CONSULTANT",
  },
  ADMIN: {
    code: "ADMIN",
    value: "ADMIN",
  },
};

const companyRoles = Object.values(rolesList);

export const SettingsSectionForm: FC<Props> = memo(({ isAdmin, isOwner, role, form, editableProps }) => {
  useEditableSubmit(form, editableProps);
  const { t } = useTranslation("session");
  const User = useContext(UserContext);
  const { contextCompany } = User;
  const isMediumScreen = useMedia(MEDIA_SIZE_MD);
  const baseClass = "consultant-profile-settings";
  return (
    <FormikForm>
      <Flex direction={isMediumScreen ? "column" : "row"} innerMargin="md" border={[false, false, true, false]}>
        {contextCompany && contextCompany.id && form.values.companyRole && (
          <Flex width={isMediumScreen ? 12 : 6} grow={1} innerMargin="sm" direction="column">
            <Flex innerMargin="sm">
              <Text size="md" color="grey1">
                {t("CONSULTANT_PROFILES.DETAIL.SETTINGS.PROFILE_ROLE.LABEL")}
              </Text>
            </Flex>
            <Flex align="center" padding={[false, false, "md", false]}>
              {isAdmin && !isOwner ? (
                <Group width={8} direction="column">
                  <FormikSelect
                    name="companyRole"
                    testName="companyRole"
                    value={form.values.companyRole}
                    required
                    options={companyRoles.map(companyRole => ({
                      ...companyRole,
                      value: t(`CONSULTANT_PROFILES.DETAIL.PROFILE_ROLE.${companyRole.code}`),
                    }))}
                    size="md"
                    simpleValue
                    isClearable={false}
                  />
                </Group>
              ) : (
                <Text size="md" color="black">
                  {t(`CONSULTANT_PROFILES.DETAIL.PROFILE_ROLE.${role}`)}
                </Text>
              )}
            </Flex>
          </Flex>
        )}

        {!contextCompany && (
          <Flex grow={1} innerMargin="sm" direction="column" className={baseClass}>
            <Flex innerMargin="sm">
              <Text size="md" color="grey1">
                {t("CONSULTANT_PROFILES.DETAIL.SETTINGS.SEARCHABLE.LABEL")}
              </Text>
            </Flex>
            <Flex align="center" padding={[false, false, "md", false]}>
              <Flex align="center" innerMargin="md">
                <Flex direction="column" grow={1} innerMargin>
                  <Label>
                    <Flex innerMargin align="center" grow={1}>
                      <FormikRadio
                        value={true}
                        name="radio"
                        onChange={() => form.setFieldValue("searchable", true, true)}
                        checked={form.values.searchable === true}
                        testName={"searchable-yes"}
                      />
                      <Flex direction="column">
                        <Text size="md" color="black">
                          {t("CONSULTANT_PROFILES.DETAIL.SETTINGS.SEARCHABLE.SEARCHABLE_ON")}
                        </Text>
                        <Text size="md">
                          {t("CONSULTANT_PROFILES.DETAIL.SETTINGS.SEARCHABLE.SEARCHABLE_ON_DESCRIPTION")}
                        </Text>
                      </Flex>
                    </Flex>
                  </Label>
                  <Label>
                    <Flex innerMargin align="flex-start">
                      <FormikRadio
                        value={false}
                        name="radio"
                        onChange={() => form.setFieldValue("searchable", false, true)}
                        checked={form.values.searchable === false}
                        testName={"searchable-no"}
                      />
                      <Flex direction="column">
                        <Text size="md" color="black">
                          {t("CONSULTANT_PROFILES.DETAIL.SETTINGS.SEARCHABLE.SEARCHABLE_OFF")}
                        </Text>
                        <Text size="md">
                          {t("CONSULTANT_PROFILES.DETAIL.SETTINGS.SEARCHABLE.SEARCHABLE_OFF_DESCRIPTION")}
                        </Text>
                      </Flex>
                    </Flex>
                  </Label>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        )}
      </Flex>
    </FormikForm>
  );
});

interface ISettingsSectionFormikProps extends IEditableSectionRenderProps {
  source: IConsultantProfile;
  onUpdate?: (consultantProfile: IConsultantProfile) => void;
  onCancel?: () => void;
  initialValues: ISettingsSectionFormValues;
  isOwner: boolean;
  isAdmin: boolean;
  isManager: boolean;
  role: string;
}

export const SettingsSectionFormik: React.FunctionComponent<ISettingsSectionFormikProps> = React.memo(
  ({ isOwner, initialValues, onUpdate, onCancel, source, ...rest }) => {
    const { addMessage } = useContext(MessagesContext);
    const User = useContext(UserContext);
    const { contextCompany } = User;
    const { t } = useTranslation(["session", "common"]);

    const handleSubmit = async (
      values: ISettingsSectionFormValues,
      actions: FormikHelpers<ISettingsSectionFormValues>,
    ) => {
      try {
        if (
          !isOwner &&
          contextCompany?.id &&
          source?.companyRole &&
          values.companyRole &&
          values.companyRole !== source.companyRole
        ) {
          await updateProfileRole(
            { profileId: source.id, companyId: contextCompany?.id },
            { companyRole: values.companyRole },
          );
        }

        const newProfile = await updateConsultantProfileSettings(
          { consultantProfileId: source.id },
          { ...source.settings, searchable: values.searchable },
        );

        if (onUpdate) {
          onUpdate(newProfile);
        }

        if (onCancel) {
          onCancel();
        }
      } catch (response) {
        addMessage({
          text: t("common:VALIDATION_MESSAGES.GENERAL"),
          type: "warning",
          context: SETTINGS_MESSENGER_CONTEXT,
        });
        applyApiErrors(actions, response.payload);
        actions.setSubmitting(false);
      }
    };
    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSettingsSchema}
        onSubmit={handleSubmit}
        render={(form: FormikProps<ISettingsSectionFormValues>) => (
          <SettingsSectionForm isOwner={isOwner} record={source} form={form} {...rest} />
        )}
      />
    );
  },
);
